/**
 * Customer Autofill System - v3 (separated checkout storage)
 * ================================================
 * Detects form fields via HTML autocomplete attributes and name patterns,
 * saves customer data to localStorage, and auto-fills on return visits.
 *
 * Contact and location drafts use separate versioned records. This prevents a
 * generic form update from reviving stale delivery details (or vice versa).
 *
 * TTL: 30 days, enforced by savedAt (same as cart-location-picker).
 */

(function() {
  'use strict';

  var LEGACY_STORAGE_KEY = 'cartDeliveryLocation';
  var LOCATION_STORAGE_BASE_KEY = 'alasil:checkout:location:v2';
  var CONTACT_STORAGE_BASE_KEY = 'alasil:checkout:contact:v2';
  var locationPage = document.getElementById('clpLocationPage');
  var loaderScript = document.currentScript;
  var storageDataset = (locationPage && locationPage.dataset) || (loaderScript && loaderScript.dataset) || {};
  var CUSTOMER_AUTHENTICATED = storageDataset.customerAuthenticated === 'true';
  var CUSTOMER_STORAGE_SCOPE = storageDataset.customerScope
    ? storageDataset.customerScope
    : (CUSTOMER_AUTHENTICATED ? 'customer' : 'guest');
  var LOCATION_STORAGE_KEY = LOCATION_STORAGE_BASE_KEY + ':' + CUSTOMER_STORAGE_SCOPE;
  var CONTACT_STORAGE_KEY = CONTACT_STORAGE_BASE_KEY + ':' + CUSTOMER_STORAGE_SCOPE;
  var STORAGE_SCHEMA_VERSION = 2;
  var STORAGE_TTL_MS = 30 * 86400000; // 30 days - matches cart-location-picker

  var CONTACT_FIELDS = {
    fullName: true, firstName: true, lastName: true, middleName: true,
    email: true, phone: true
  };

  // Field mappings: autocomplete attribute -> storage key
  var FIELD_MAPPINGS = {
    'name': 'fullName',
    'given-name': 'firstName',
    'family-name': 'lastName',
    'additional-name': 'middleName',
    'email': 'email',
    'tel': 'phone',
    'tel-national': 'phone',
    'street-address': 'address1',
    'address-line1': 'address1',
    'address-line2': 'address2',
    'address-level1': 'province',
    'address-level2': 'city',
    'postal-code': 'zip',
    'country': 'country',
    'country-name': 'countryName',
    'organization': 'company'
  };

  // Input name patterns to detect field types
  var NAME_PATTERNS = {
    email: /email|e-mail/i,
    phone: /phone|tel|mobile/i,
    firstName: /first.?name|given.?name|fname/i,
    lastName: /last.?name|family.?name|surname|lname/i,
    fullName: /^name$|full.?name|your.?name/i,
    address1: /address.?1|street|address(?!.*2)/i,
    address2: /address.?2|apt|suite|unit/i,
    city: /city|town|locality/i,
    province: /province|state|region/i,
    zip: /zip|postal|postcode/i,
    country: /country/i,
    company: /company|organization|business/i
  };

  // -- Storage helpers (versioned contact + location records) --

  function draftStorage() {
    return CUSTOMER_AUTHENTICATED ? localStorage : sessionStorage;
  }

  function readRecord(key) {
    try {
      var storage = draftStorage();
      var raw = storage.getItem(key);
      if (!raw) return null;
      var data = JSON.parse(raw);
      if (!data || typeof data !== 'object' || Number(data.schemaVersion) !== STORAGE_SCHEMA_VERSION) {
        storage.removeItem(key);
        return null;
      }
      var savedAt = Number(data.savedAt || 0);
      if (!savedAt || (Date.now() - savedAt) > STORAGE_TTL_MS) {
        storage.removeItem(key);
        return null;
      }
      return data;
    } catch (e) {
      return null;
    }
  }

  function writeRecord(key, fields) {
    try {
      var existing = readRecord(key) || {};
      var merged = { schemaVersion: STORAGE_SCHEMA_VERSION, savedAt: Date.now() };
      var k;
      for (k in existing) {
        if (Object.prototype.hasOwnProperty.call(existing, k) && k !== 'schemaVersion' && k !== 'savedAt') merged[k] = existing[k];
      }
      for (k in fields) {
        if (Object.prototype.hasOwnProperty.call(fields, k)) merged[k] = fields[k] == null ? '' : fields[k];
      }
      draftStorage().setItem(key, JSON.stringify(merged));
    } catch (e) {
      /* silent - private mode / quota */
    }
  }

  function getStored() {
    var location = readRecord(LOCATION_STORAGE_KEY) || {};
    var contact = readRecord(CONTACT_STORAGE_KEY) || {};
    var merged = {};
    var k;
    for (k in location) if (Object.prototype.hasOwnProperty.call(location, k)) merged[k] = location[k];
    for (k in contact) if (Object.prototype.hasOwnProperty.call(contact, k)) merged[k] = contact[k];
    if (merged.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(String(merged.email).trim())) merged.email = '';
    if (merged.phone) {
      var phone = String(merged.phone).trim();
      var phoneDigits = phone.replace(/\D/g, '');
      if (!/^\+?[\d\s().-]+$/.test(phone) || phoneDigits.length < 8 || phoneDigits.length > 15) merged.phone = '';
    }
    ['building', 'unit', 'address1', 'address2'].forEach(function(field) {
      if (!Object.prototype.hasOwnProperty.call(merged, field)) return;
      var value = merged[field];
      var normalized = (typeof value === 'string' || typeof value === 'number')
        ? String(value).replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/\s+/g, ' ').trim()
        : '';
      merged[field] = (!normalized || normalized.length > 120 || /^\d{9,}$/.test(normalized)) ? '' : normalized;
    });
    return Object.keys(merged).length ? merged : null;
  }

  function saveFields(newFields) {
    if (!newFields || typeof newFields !== 'object') return;
    var contact = {};
    var location = {};
    for (var k in newFields) {
      if (!Object.prototype.hasOwnProperty.call(newFields, k)) continue;
      if (CONTACT_FIELDS[k]) contact[k] = newFields[k];
      else location[k] = newFields[k];
    }
    ['building', 'unit', 'address1', 'address2'].forEach(function(field) {
      if (!Object.prototype.hasOwnProperty.call(location, field)) return;
      var value = location[field];
      var normalized = (typeof value === 'string' || typeof value === 'number')
        ? String(value).replace(/[\u0000-\u001F\u007F]/g, ' ').replace(/\s+/g, ' ').trim()
        : '';
      location[field] = (!normalized || normalized.length > 120 || /^\d{9,}$/.test(normalized)) ? '' : normalized;
    });
    if (Object.keys(contact).length) writeRecord(CONTACT_STORAGE_KEY, contact);
    if (Object.keys(location).length) writeRecord(LOCATION_STORAGE_KEY, location);
  }

  // -- One-time migration from old shopify_customer_autofill key --

  function migrateOldKey() {
    try {
      // Unscoped legacy records have no trustworthy owner. Purge rather than
      // risk assigning one shopper's contact/address data to another account.
      localStorage.removeItem(LOCATION_STORAGE_BASE_KEY);
      localStorage.removeItem(CONTACT_STORAGE_BASE_KEY);
      localStorage.removeItem(LEGACY_STORAGE_KEY);
      var OLD_KEY = 'shopify_customer_autofill';
      var raw = localStorage.getItem(OLD_KEY);
      if (!raw) return;
      // The pre-v3 record was also global and cannot be safely attributed.
      localStorage.removeItem(OLD_KEY);
    } catch (e) {
      try { localStorage.removeItem('shopify_customer_autofill'); } catch (e2) { /* silent */ }
    }
  }

  // -- Field detection --

  function detectFieldType(input) {
    var autocomplete = input.getAttribute('autocomplete');
    if (autocomplete && FIELD_MAPPINGS[autocomplete]) return FIELD_MAPPINGS[autocomplete];
    if (input.type === 'email') return 'email';
    if (input.type === 'tel') return 'phone';
    var identifiers = [input.name || '', input.id || '', input.placeholder || '', input.getAttribute('aria-label') || ''].join(' ');
    for (var fieldType in NAME_PATTERNS) {
      if (Object.prototype.hasOwnProperty.call(NAME_PATTERNS, fieldType) && NAME_PATTERNS[fieldType].test(identifiers)) {
        return fieldType;
      }
    }
    return null;
  }

  // -- Autofill --

  function autofillForms() {
    var data = getStored();
    if (!data) return;
    var inputs = document.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="checkbox"]):not([type="radio"]), textarea');
    inputs.forEach(function(input) {
      if (input.value) return;
      if (input.closest && input.closest('.cart-item, .product-form, .quantity-input')) return;
      // The cart checkout flow owns all #clp* fields, including its scoped
      // draft storage and E.164 phone normalization. A second autofill writer
      // can restore a value immediately after the shopper clears it and make
      // validation appear inconsistent.
      if (input.id && /^clp/.test(input.id)) return;
      var fieldType = detectFieldType(input);
      if (fieldType && data[fieldType]) {
        input.value = data[fieldType];
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  // -- Watch & save --

  function watchInputBlur() {
    document.addEventListener('blur', function(e) {
      var input = e.target;
      if (!input || !input.matches || !input.matches('input, textarea')) return;
      if (input.type === 'password') return;
      if (input.name && /password|card|cvv|cvc/i.test(input.name)) return;
      if (input.closest && input.closest('.cart-item, .product-form, .quantity-input')) return;
      // Skip CLP-managed inputs - cart-location-picker.js saves these
      // with proper E.164 normalization; raw input values would corrupt the phone format
      if (input.id && /^clp/.test(input.id)) return;
      var fieldType = detectFieldType(input);
      if (fieldType) {
        var fields = {};
        // Persist blanks as intentional clears. Otherwise a user who deletes a
        // stale value sees it return on the next form render.
        fields[fieldType] = input.value ? input.value.trim() : '';
        saveFields(fields);
      }
    }, true);
  }

  function watchFormSubmissions() {
    document.addEventListener('submit', function(e) {
      var form = e.target;
      if (form.closest && form.closest('.cart-item, .product-form, .clp-form')) return;
      if (form.action && form.action.indexOf('/cart') !== -1) return;
      var inputs = form.querySelectorAll('input:not([type="hidden"]):not([type="submit"]):not([type="button"]):not([type="password"]), textarea');
      var fields = {};
      inputs.forEach(function(input) {
        if (!input.value || input.value.trim() === '') return;
        // Skip cart-location-picker's own inputs - it stores phone/email/etc with
        // proper normalization (E.164); a raw form value here would corrupt them.
        if (input.id && /^clp/.test(input.id)) return;
        if (input.name && /password|card|cvv|cvc|cc-/i.test(input.name)) return;
        var fieldType = detectFieldType(input);
        if (fieldType) fields[fieldType] = input.value.trim();
      });
      if (Object.keys(fields).length > 0) saveFields(fields);
    });
  }

  // -- Enhance autocomplete attributes on inputs that lack them --

  function enhanceAutocomplete() {
    var map = {
      email:'email', phone:'tel', firstName:'given-name', lastName:'family-name',
      fullName:'name', address1:'address-line1', address2:'address-line2',
      city:'address-level2', province:'address-level1', zip:'postal-code',
      country:'country-name', company:'organization'
    };
    var inputs = document.querySelectorAll('input:not([autocomplete]), textarea:not([autocomplete])');
    inputs.forEach(function(input) {
      var fieldType = detectFieldType(input);
      if (fieldType && map[fieldType]) input.setAttribute('autocomplete', map[fieldType]);
    });
  }

  // -- Init --

  // Module-level observer ref - init() can run twice (initial + 1s refresh pass).
  // Without disconnecting the previous instance, the second call would stack a
  // duplicate MutationObserver on document body and re-fire enhanceAutocomplete/autofillForms
  // on every DOM mutation.
  var _formObserver = null;
  var _watchFormSubmissionsBound = false;
  var _watchInputBlurBound = false;

  function init() {
    migrateOldKey();
    enhanceAutocomplete();
    autofillForms();
    // Guard the document-level submit/blur listeners - they don't need re-binding.
    if (!_watchFormSubmissionsBound) { _watchFormSubmissionsBound = true; watchFormSubmissions(); }
    if (!_watchInputBlurBound) { _watchInputBlurBound = true; watchInputBlur(); }

    if (_formObserver) { try { _formObserver.disconnect(); } catch (e) {} _formObserver = null; }

    var debounce;
    var observer = new MutationObserver(function(mutations) {
      for (var i = 0; i < mutations.length; i++) {
        for (var j = 0; j < mutations[i].addedNodes.length; j++) {
          var node = mutations[i].addedNodes[j];
          if (node.nodeType === 1 && ((node.matches && node.matches('form')) || (node.querySelector && node.querySelector('form')))) {
            clearTimeout(debounce);
            debounce = setTimeout(function() { enhanceAutocomplete(); autofillForms(); }, 200);
            return;
          }
        }
      }
    });
    var target = document.getElementById('MainContent') || document.body;
    observer.observe(target, { childList: true, subtree: true });
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer !== target) observer.observe(drawer, { childList: true, subtree: true });
    _formObserver = observer;
  }

  // Run once on DOM ready, plus a single 1s delayed re-run for late-mounted forms (Theme Editor section reloads, async drawers).
  // Previously `init()` ran twice unconditionally on every load (once immediately + once via the setTimeout).
  var _initRan = false;
  function runInitOnce() { if (!_initRan) { _initRan = true; init(); } }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitOnce, { once: true });
  } else {
    runInitOnce();
  }
  // Refresh pass for late-mounted forms: only re-scan (enhance + autofill); the
  // full init() (migrate + observer setup + listener binding) already ran once,
  // so re-running it would just repeat that work.
  setTimeout(function () {
    if (!_initRan) { runInitOnce(); return; }
    enhanceAutocomplete();
    autofillForms();
  }, 1000);
  window.addEventListener('pagehide', function () {
    if (_formObserver) { try { _formObserver.disconnect(); } catch (e) {} _formObserver = null; }
  }, { once: true });
})();
